import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import {
    EuiButton, EuiFieldNumber,
    EuiFieldText,
    EuiIcon,
    EuiFormRow,
    EuiSelect,
    EuiSwitch, EuiTextArea
} from '@elastic/eui';
import { FooterButtons } from '../../ServerEdit.page';
import { addToast } from '../../../../components/toasts/Toast';
import {
    buildUptimePayload,
    isValidCustomInterval,
    toUptimeNumber
} from '../../createNewMonitor/uptimePayload';
import {
    HEART_BEAT_TYPES,
    ITERVAL_OPTIONS
} from '../../createNewMonitor/CreateNewMonitorComponent';
import validationsService from '../../../../../servises/validations.service';
import uptimeStore from '../../../../../stores/uptime.store';

const Styles = styled.div`
  display: grid;
  grid-gap: 14px;

  .left {
    text-align: left;
    margin-bottom: 4px;
  }

  .euiFormRow {
    margin-bottom: 0;
  }
`;

const FormGridStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  &.one-row {
    grid-template-columns: 1fr;
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FormSectionStyled = styled.section`
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 14px;

  .section-title {
    align-items: center;
    color: rgba(255, 255, 255, 0.88);
    display: flex;
    font-size: 14px;
    font-weight: 700;
    gap: 7px;
    margin-bottom: 12px;
  }

  .section-help {
    color: rgba(255, 255, 255, 0.52);
    font-size: 12px;
    line-height: 1.45;
    margin: -5px 0 14px;
  }
`;

const SwitchRowStyled = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.055);
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;

  .title {
    color: rgba(255, 255, 255, 0.86);
    display: block;
    font-size: 13px;
    font-weight: 700;
  }

  .description {
    color: rgba(255, 255, 255, 0.5);
    display: block;
    font-size: 11px;
    line-height: 1.4;
    margin-top: 3px;
  }
`;

const TextAreaGridStyled = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const EditUptimeData = ({ uptime }) => {
    const [isSendingSave, setIsSendingSave] = useState(false);
    const [uptimeName, setUptimeName] = useState(uptime.name);
    const [allDisabled, setAllDisabled] = useState(false);
    const [isCustomInterval, setIsCustomIterval] = useState(uptime.isCustomInterval);
    const [customInterval, setCustomIterval] = useState(uptime.customInterval);
    const [interval, setIterval] = useState(null);
    const [url, setUrl] = useState(uptime.url);
    const [validHttpStatus, setValidHttpStatus] = useState(uptime.validHttpStatus);
    const [method, setMethod] = useState(uptime.method);
    const [body, setBody] = useState(uptime.body);
    const [header, setHeader] = useState(uptime.header);

    const heartBeatType = uptime.type;
    const uptimeId = uptime._id;
    const isTargetValid = heartBeatType === HEART_BEAT_TYPES.HTTP
        ? validationsService.validateUrl(url) && validationsService.validateHttpStatus(validHttpStatus)
        : validationsService.validateIp(url);
    const isIntervalValid = !isCustomInterval || isValidCustomInterval(customInterval);

    useEffect(() => {
        if (!uptime.isCustomInterval) {
            const intervalValue = ITERVAL_OPTIONS.find(item => item.interval === uptime.interval);
            if (intervalValue) {
                setIterval(intervalValue.text);
            }
        }
    }, [uptime.interval, uptime.isCustomInterval]);

    const handleSubmit = useCallback(async () => {
        if (isSendingSave) {
            return;
        }

        setIsSendingSave(true);
        setAllDisabled(true);

        let intervalInSeconds = 0;
        if (!isCustomInterval) {
            const intervalValue = ITERVAL_OPTIONS.find(item => item.text === interval);
            intervalInSeconds = intervalValue ? intervalValue.interval : uptime.interval;
        }

        try {
            await uptimeStore.updateUptime(uptimeId, buildUptimePayload({
                name: uptimeName,
                type: heartBeatType,
                url,
                validHttpStatus,
                method,
                body,
                header,
                isCustomInterval,
                customInterval,
                interval: intervalInSeconds
            }));
            addToast('Uptime changes saved', 'suc');
        } catch (error) {
            addToast(error.message || 'Failed to save uptime changes', 'error');
        } finally {
            setIsSendingSave(false);
            setAllDisabled(false);
        }
    }, [
        isSendingSave,
        uptimeId,
        heartBeatType,
        uptime.interval,
        uptimeName,
        method,
        url,
        interval,
        customInterval,
        isCustomInterval,
        validHttpStatus,
        body,
        header]);

    const change = (type, val) => {
        if (type === 'name') {
            setUptimeName(val);
        }

        if (type === 'customInterval') {
            setCustomIterval(toUptimeNumber(val));
        }
    };

    return (
        <Styles>
            <FormSectionStyled>
                <div className="section-title">
                    <EuiIcon type="documentEdit" size="s"/>
                    Identity
                </div>
                <EuiFormRow label="Monitor name">
                    <EuiFieldText
                        fullWidth={ true }
                        value={ uptimeName }
                        disabled={ allDisabled }
                        isInvalid={ false }
                        placeholder="Public API"
                        onChange={ e => setUptimeName(e.target.value) }
                    />
                </EuiFormRow>
            </FormSectionStyled>

            <FormSectionStyled>
                <div className="section-title">
                    <EuiIcon type="clock" size="s"/>
                    Schedule
                </div>
                <p className="section-help">Choose a preset interval or enter a custom interval in seconds.</p>
                <FormGridStyled>
                    <SwitchRowStyled>
                        <span>
                            <span className="title">Custom interval</span>
                            <span className="description">Use seconds instead of a preset schedule.</span>
                        </span>
                        <EuiSwitch
                            disabled={ allDisabled }
                            label={ isCustomInterval ? 'On' : 'Off' }
                            checked={ isCustomInterval }
                            onChange={ () => setIsCustomIterval(
                                !isCustomInterval) }
                        />
                    </SwitchRowStyled>

                    <EuiFormRow
                        label="Interval"
                    >
                        <EuiSelect
                            isDisabled={ isCustomInterval || allDisabled }
                            placeholder="Select an option"
                            options={ ITERVAL_OPTIONS }
                            value={ interval }
                            onChange={ (e) => setIterval(e.target.value) }
                        />
                    </EuiFormRow>
                    <EuiFormRow fullWidth={ true }
                                label="Custom interval">
                        <EuiFieldNumber
                            fullWidth={ true }
                            append="seconds"
                            min={ 60 }
                            step={ 1 }
                            value={ customInterval }
                            disabled={ !isCustomInterval || allDisabled }
                            isInvalid={ isCustomInterval && !isValidCustomInterval(customInterval) }
                            onChange={ e => change('customInterval',
                                e.target.value
                            ) }
                        />
                    </EuiFormRow>
                </FormGridStyled>
            </FormSectionStyled>

            { heartBeatType === HEART_BEAT_TYPES.HTTP && (
                <>
                    <FormSectionStyled>
                        <div className="section-title">
                            <EuiIcon type="globe" size="s"/>
                            Target endpoint
                        </div>
                        <EuiFormRow fullWidth={ true } label="URL">
                            <EuiFieldText
                                disabled={ allDisabled }
                                fullWidth={ true }
                                isInvalid={ !validationsService.validateUrl(url) }
                                placeholder="http://"
                                value={ url }
                                onChange={ e => setUrl(e.target.value) }
                            />
                        </EuiFormRow>
                    </FormSectionStyled>

                    <FormSectionStyled>
                        <div className="section-title">
                            <EuiIcon type="check" size="s"/>
                            Response rules
                        </div>
                        <FormGridStyled>
                        <EuiFormRow
                            fullWidth={ true }
                            label="Valid HTTP status"
                        >
                            <EuiFieldNumber
                                disabled={ allDisabled }
                                fullWidth={ true }
                                min={ 0 }
                                max={ 599 }
                                isInvalid={ !validationsService.validateHttpStatus(validHttpStatus) }
                                value={ validHttpStatus }
                                onChange={ e => setValidHttpStatus(toUptimeNumber(e.target.value)) }
                            />
                        </EuiFormRow>

                        <EuiFormRow
                            label="HTTP method"
                        >
                            <EuiSelect
                                placeholder="Select an option"
                                disabled={ allDisabled }
                                options={ [
                                    {
                                        key: 'GET',
                                        text: 'GET'
                                    },
                                    {
                                        key: 'POST',
                                        text: 'POST'
                                    },
                                    {
                                        key: 'PUT',
                                        text: 'PUT'
                                    },
                                    {
                                        key: 'PATCH',
                                        text: 'PATCH'
                                    },
                                    {
                                        key: 'DELETE',
                                        text: 'DELETE'
                                    },
                                    {
                                        key: 'HEAD',
                                        text: 'HEAD'
                                    },
                                    {
                                        key: 'OPTIONS',
                                        text: 'OPTIONS'
                                    }
                                ] }
                                value={ method }
                                onChange={ (e) => setMethod(e.target.value) }
                            />
                        </EuiFormRow>
                        </FormGridStyled>
                    </FormSectionStyled>

                    <FormSectionStyled>
                        <div className="section-title">
                            <EuiIcon type="editorCodeBlock" size="s"/>
                            Request payload
                        </div>
                        <p className="section-help">Optional JSON body and headers for requests that need more context.</p>
                        <TextAreaGridStyled>
                        <EuiFormRow fullWidth={ true }
                                    label="Body (JSON)">
                            <EuiTextArea
                                disabled={ allDisabled }
                                fullWidth={ true }
                                placeholder={ 'Example:\n' +
                                    '{\n' +
                                    '    "key": "value"\n' +
                                    '}' }
                                value={ body }
                                onChange={ e => setBody(
                                    e.target.value) }
                            />
                        </EuiFormRow>

                        <EuiFormRow fullWidth={ true }
                                    label="Headers (JSON)">
                            <EuiTextArea
                                disabled={ allDisabled }
                                fullWidth={ true }
                                placeholder={ 'Example:\n' +
                                    '{\n' +
                                    '    "HeaderName": "HeaderValue"\n' +
                                    '}' }
                                value={ header }
                                onChange={ e => setHeader(
                                    e.target.value) }
                            />
                        </EuiFormRow>
                        </TextAreaGridStyled>
                    </FormSectionStyled>
                </>
            ) }

            { heartBeatType === HEART_BEAT_TYPES.PING && (
                <FormSectionStyled>
                    <div className="section-title">
                        <EuiIcon type="ip" size="s"/>
                        Target host
                    </div>
                    <EuiFormRow fullWidth={ true } label="IP">
                        <EuiFieldText
                            disabled={ allDisabled }
                            fullWidth={ true }
                            isInvalid={ !validationsService.validateIp(
                                url) }
                            placeholder="127.0 0.1"
                            value={ url }
                            onChange={ e => setUrl(
                                e.target.value) }
                        />
                    </EuiFormRow>
                </FormSectionStyled>
            ) }

            <FooterButtons className={ 'left' }>
                <EuiButton
                    iconType="save"
                    isDisabled={ !uptimeName || !isTargetValid || !isIntervalValid }
                    size="s"
                    onClick={ handleSubmit }
                    fill
                    isLoading={ isSendingSave }
                >Save
                </EuiButton>
            </FooterButtons>
        </Styles>
    );
};

export default EditUptimeData;
